import { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import { resources } from "@constants/hardcoded-strings";
import { DEPRECATED_TRUST_SCORE_SERVICE_OFFERING_ID } from "@constants/verification-service-details-constants";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { TrustScoreErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { FormattedRpServiceConfigurationScoreModifier } from "@idw-types/data/rp-service-configuration/rp-service-configuration-score";
import { ScoreReferenceData } from "@idw-types/data/rp-service-configuration/scores/score-reference-data";
import {
    isTrustScoreEnabled,
    updateServiceOfferingsAndTrustScoreModifiers
} from "@utils/scores/score-util";
import { getScoreServiceOfferings, hasAtLeastOneTrustScoreService } from "@utils/scores/trust-score-util";
import { TrustScoreModifiersTableContainer } from "./trust-score-modifiers-table-container/trust-score-modifiers-table-container";
import { ServiceOfferingWeightsContainer } from './service-offering-weights/service-offering-weights-container';
import commonStyles from "../../verification-service-details-component.less";
import styles from "./trust-score-tab-container.less";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type TrustScoreTabContainerProps = {
    isDisabled: boolean;
    validationErrors: TrustScoreErrors;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    trustScoreServiceOfferingIDs: Array<string>;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    scoreReferenceData: ScoreReferenceData;
    scoreServiceOfferingIDs: Array<string>;
    setRpServiceConfigurationState: (
        state: FormattedRpServiceConfiguration
    ) => void;
};

export const TrustScoreTabContainer = (
    props: TrustScoreTabContainerProps
) => {
    const {
        isDisabled,
        validationErrors,
        rpServiceConfigurationState,
        assertionStatusMaps,
        trustScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        setRpServiceConfigurationState,
        scoreReferenceData
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);

    const shouldEnableTrustScoreToggle = useMemo(() => {
        return isTrustScoreEnabled(
            rpServiceConfigurationState.serviceOfferings,
            trustScoreServiceOfferingIDs
        );
    }, [rpServiceConfigurationState.serviceOfferings]);

    const handleShowCanViewTrustScoreModifiersChange = () => {
        const {
            serviceOfferings,
            trustScoreModifiers
        } = updateServiceOfferingsAndTrustScoreModifiers(
            rpServiceConfigurationState,
            assertionStatusMaps,
            trustScoreServiceOfferingIDs,
            !shouldEnableTrustScoreToggle
        );

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            serviceOfferings,
            trustScoreModifiers
        });
    };

    const setTrustScoreModifiersState = (
        trustScoreModifiers: Array<
            FormattedRpServiceConfigurationScoreModifier
        >
    ) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            trustScoreModifiers
        });
    };

    const hasOneTrustScoreServiceAtLeast = useMemo(() => {
        return hasAtLeastOneTrustScoreService(rpServiceConfigurationState.serviceOfferings, scoreServiceOfferingIDs);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const requiredScoreServiceOfferings = useMemo(() => {
        return getScoreServiceOfferings(rpServiceConfigurationState.serviceOfferings, scoreServiceOfferingIDs);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const deprecatedTrustScoreEnabled = useMemo(() => {
        return !!rpServiceConfigurationState.serviceOfferings.find(so =>
            so.serviceOfferingID === DEPRECATED_TRUST_SCORE_SERVICE_OFFERING_ID &&
            so.addedToService &&
            so.status === EntityStatusTypes.ACTIVE
        );
    }, [rpServiceConfigurationState.serviceOfferings]);

    const isToggleChecked = useMemo(() => {
        return shouldEnableTrustScoreToggle && !deprecatedTrustScoreEnabled
    }, [shouldEnableTrustScoreToggle, deprecatedTrustScoreEnabled])

    const trustScoreResources = resources.pages.verificationServiceDetails.tabs.scores.subTabs.trustScore;

    return (
        <div>
            <ContentPageHeader
                title={trustScoreResources.title}
                headerButton={
                    <ToggleFormGroup
                        className={styles.assertionScoreToggleForm}
                        controlId={"enable-trust-score-toggle"}
                        automationId={"enable-trust-score-toggle"}
                        label={"Enable Trust Score"}
                        ariaLabel={"Enable Trust Score"}
                        checkedIcon={false}
                        uncheckedIcon={false}
                        labelWidth={labelWidth}
                        checked={isToggleChecked}
                        disabled={
                            isDisabled ||
                            (!hasOneTrustScoreServiceAtLeast && !isToggleChecked)
                        }
                        isRequired={false}
                        onChange={handleShowCanViewTrustScoreModifiersChange}
                        value={`${isToggleChecked}`}
                    />
                }
            />
            <ContentPageBody isMaxWidth={true}>
                <div>
                    <span className={commonStyles.description}>
                        {trustScoreResources.description}
                    </span>
                    {deprecatedTrustScoreEnabled && (
                        <Alert variant={"warning"}>{trustScoreResources.deprecatedWarningMessage}</Alert>
                    )}
                    {!shouldEnableTrustScoreToggle && !hasOneTrustScoreServiceAtLeast && !deprecatedTrustScoreEnabled && (
                        <Alert variant="warning" id="has-at-least-one-trust-score-service-warning">
                            <>
                                <p>
                                    {trustScoreResources.sections.serviceOfferingWeights.error.noScoreEnableWarningMessage}
                                </p>
                                <ul>
                                    {requiredScoreServiceOfferings.map((so, index) =>
                                        <li key={index}>{so.name}</li>
                                    )}
                                </ul>
                                <p> {trustScoreResources.sections.serviceOfferingWeights.error.noScoreEnableWarningMessage2}</p>
                            </>
                        </Alert>
                    )}
                    {shouldEnableTrustScoreToggle && !deprecatedTrustScoreEnabled && (
                        <>
                            <ServiceOfferingWeightsContainer
                                isDisabled={isDisabled}
                                validationErrors={validationErrors}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                            <br />
                            {rpServiceConfigurationState.trustScoreModifiers.length ===
                                0 && (
                                    <Alert variant={"info"}>
                                        {trustScoreResources.sections.trustScoreModifiers.noTrustScoreModifierLabel}
                                    </Alert>
                                )}

                            {rpServiceConfigurationState.trustScoreModifiers.length >
                                0 && (
                                    <TrustScoreModifiersTableContainer
                                        scoreReferenceData={scoreReferenceData}
                                        isDisabled={isDisabled}
                                        trustScoreModifiers={
                                            rpServiceConfigurationState.trustScoreModifiers
                                        }
                                        canViewPassTrustScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewPassTrustScoreModifiers
                                        }
                                        canViewCloseTrustScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewCloseTrustScoreModifiers
                                        }
                                        canViewPartialTrustScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewPartialTrustScoreModifiers
                                        }
                                        canViewUnverifiedTrustScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewUnverifiedTrustScoreModifiers
                                        }
                                        canViewFailTrustScoreModifiers={
                                            rpServiceConfigurationState.permissions.canViewFailTrustScoreModifiers
                                        }
                                        formattedServiceOfferings={
                                            rpServiceConfigurationState.serviceOfferings
                                        }
                                        setTrustScoreModifiersState={
                                            setTrustScoreModifiersState
                                        }
                                    />
                                )}
                        </>
                    )}
                </div>
            </ContentPageBody>
        </div>
    );
};
